import { InteractionType } from "@azure/msal-browser";
import {
  IMsalContext,
  MsalAuthenticationResult,
  MsalAuthenticationTemplate,
  useMsal,
} from "@azure/msal-react";
import React from "react";

import {
  b2cPolicies,
  loginRequestScopes,
} from "../../app/configs/appConfig";
import { LottieLoading } from "../../components/graphics/LottieLoading";
import {
  isRestartError,
  restartPending,
} from "../../pages/main/signInRestart";
import { ScrollToTop } from "../../support/ScrollToTop";
import { LoadingMainView } from "./LoadingMainView";

const MsalLoadingComponent: React.FC<IMsalContext> = () => {
  return <LottieLoading />;
};

/**
 * Shown instead of a crash when sign-in comes back with an error. When the
 * person only cancelled a B2C step (e.g. Cancel on "Forgot your password?"),
 * MsalEventsHandler is already starting the sign-in again, so we just show
 * the loading animation. Otherwise (or if restarting was refused to avoid a
 * loop) the person gets a "Back to sign in" button.
 */
export const MsalErrorComponent: React.FC<MsalAuthenticationResult> = ({
  error,
}) => {
  const { instance } = useMsal();
  const [busy, setBusy] = React.useState(false);
  const cancelled = isRestartError(error);

  if (cancelled && restartPending()) {
    return <LottieLoading />;
  }

  const backToSignIn = () => {
    setBusy(true);
    instance
      .loginRedirect({
        ...loginRequestScopes,
        authority: b2cPolicies.authorities.signUpSignIn.authority,
      })
      .catch(() => {
        // An interaction may still be finishing; a page reload always works.
        window.location.assign("/");
      });
  };

  return (
    <div className="flex min-h-screen w-full items-center justify-center bg-[#F6F7FB] p-4">
      <div className="w-full max-w-[420px] rounded-[16px] border border-[#DCE2EA] bg-white p-6 text-center shadow-sm">
        <h1 className="text-lg font-semibold text-[#1F2433]">
          {cancelled ? "Sign-in was cancelled" : "We couldn't sign you in"}
        </h1>
        <p className="mt-2 text-sm text-[#5B6478]">
          {cancelled
            ? "No problem. Go back to the sign-in page to continue."
            : "Something went wrong while signing in. Please try again."}
        </p>
        <button
          type="button"
          disabled={busy}
          onClick={backToSignIn}
          className="mt-5 inline-flex h-10 items-center justify-center rounded-[10px] bg-[#6B4EFF] px-5 text-sm font-semibold text-white hover:bg-[#5A3FE6] disabled:opacity-60">
          {busy ? "Opening sign-in…" : "Back to sign in"}
        </button>
      </div>
    </div>
  );
};

export const AuthenticatedMainView: React.FC = () => {
  const authRequest = {
    ...loginRequestScopes,
  };

  return (
    <>
      <ScrollToTop />

      <MsalAuthenticationTemplate
        interactionType={InteractionType.Redirect}
        authenticationRequest={authRequest}
        loadingComponent={MsalLoadingComponent}
        errorComponent={MsalErrorComponent}>
        <LoadingMainView />
      </MsalAuthenticationTemplate>
    </>
  );
};
